import type { LeadStage } from './types';

/**
 * The lead statuses, asked for by key rather than recognised by name.
 *
 * ── Why this file exists ───────────────────────────────────────────────────
 *
 * Before the restructure, nine places in the Tracker decided what a status
 * MEANT by reading what it was CALLED:
 *
 *   s.name === 'Cold'
 *   s.name === 'Active Deal' || s.name === 'Deal' || s.name === 'Qualified'
 *   stageNameLower(l.stage_id).includes('talk')
 *   ...includes('deal')
 *   ...includes('won')
 *
 * Every one of those is a rule written in a place nobody looks, keyed on a
 * string somebody can edit. Renaming a status silently broke the dashboard
 * KPIs, the funnel, the backlog split, the New Lead default, the Import
 * default and the auto-promote-on-reply — all without a single error, because
 * `find` returning undefined is a valid answer everywhere it was used.
 *
 * `.includes('deal')` was the sharpest edge: under the new vocabulary
 * "Converted to Deal" contains "deal", and so would any future status with
 * the word in it, so the old KPI would have started counting a different
 * population the day the names changed.
 *
 * So 20261122000100 gave lead_stages a `key`. It is what code asks for; the
 * name is what people read and may be renamed freely. This module is the only
 * place that knows the keys.
 *
 * ── The unknown-status rule ────────────────────────────────────────────────
 *
 * Every lookup here returns null rather than guessing, and every caller has
 * to decide what null means for it. That is deliberate: the old code's
 * fallbacks — `?? stages[0]`, `?? null` then silently counting zero — are
 * what let a broken vocabulary look like an empty pipeline.
 */

/** The five, in the order work moves through them. */
export const STAGE_KEYS = [
  'new',
  'contacted',
  // Drop 143. Two statuses somebody sets by hand, added because the funnel
  // could say what happened to a lead and not where the living ones are
  // sitting: 325 leads read as Contacted while nobody was working them.
  // Kyu, 14 September: "Manual. We determine manually which stage they should
  // be in."
  'followup',
  'engaged',
  'nurture',
  'converted',
  'closed',
] as const;

export type StageKey = (typeof STAGE_KEYS)[number];

/** The status row for a key, or null when the vocabulary does not have it. */
export function stageByKey(stages: LeadStage[], key: StageKey): LeadStage | null {
  return stages.find((stage) => stageKeyOf(stage) === key) ?? null;
}

/** A status row's key, lower-cased and trimmed. '' when it has none. */
export function stageKeyOf(stage: LeadStage): string {
  return (stage.key ?? '').trim().toLowerCase();
}

/** The key a lead is sitting on, or null when its status is unrecognised. */
export function leadStageKey(stageId: string, stages: LeadStage[]): StageKey | null {
  const found = stages.find((stage) => stage.id === stageId);
  if (found === undefined) {
    return null;
  }
  const key = stageKeyOf(found);
  return (STAGE_KEYS as readonly string[]).includes(key) ? (key as StageKey) : null;
}

/** Leads sitting on one status. */
export function leadsInStage<T extends { stage_id: string }>(
  leads: T[],
  stages: LeadStage[],
  key: StageKey,
): T[] {
  return leads.filter((lead) => leadStageKey(lead.stage_id, stages) === key);
}

/**
 * Where a brand-new lead starts.
 *
 * Null when the vocabulary has no 'new', which is a broken database rather
 * than an ordinary state — the old code fell back to `stages[0]`, which
 * meant a mis-seeded vocabulary silently filed every new lead under whatever
 * happened to sort first.
 */
export function newLeadStage(stages: LeadStage[]): LeadStage | null {
  return stageByKey(stages, 'new');
}

/**
 * The status a reply moves a lead to.
 *
 * Kyu's rule: "Contacted → Engaged: reply received, inbound LinkedIn message,
 * or call booked." One name for it, so the Lists grid and the timeline cannot
 * promote a lead to two different places.
 */
export function repliedStage(stages: LeadStage[]): LeadStage | null {
  return stageByKey(stages, 'engaged');
}

/**
 * Whether this lead has been contacted at all.
 *
 * The rule behind "awaiting first touch", stated once. The Dashboard used to
 * work it out inline from list memberships, which is why it raised the alert
 * for people Nez had already emailed outside a list — the complaint that
 * started the whole restructure.
 *
 * Deliberately reads the STATUS, not the memberships: from batch 2 the status
 * is moved by real recorded activity, so this stays true for a lead emailed
 * outside any list.
 */
export function awaitingFirstTouch<T extends { stage_id: string }>(
  lead: T,
  stages: LeadStage[],
): boolean {
  return leadStageKey(lead.stage_id, stages) === 'new';
}
